var AbstractVideoPlayer = require("app/MediaPlayer/Tech/Abstract/AbstractVideoPlayer"),
    Loader              = require("./Loader"),
    config              = require("app/config"),
    frameThrottle       = require("enyo-perf/utils").frameThrottle,
    get                 = require("object-path").get,
    kind                = require("enyo/kind"),
    logger              = require("enyo/logger"),
    quickMacroize       = require("enyo/macroize").quickMacroize;

require("./Player.less");

module.exports = kind(/** @lends  app.MediaPlayer.Tech.YouTubePlayer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.YouTubePlayer",

    /**
     * @private
     */
    kind: AbstractVideoPlayer,

    /**
     * @private
     */
    classes: "app-media-player-youtube-player",

    /**
     * @private
     */
    supportsSafeArea: false,

    /**
     * @type {boolean}
     * @private
     */
    isReusable: true,

    /**
     * @private
     */
    isLoadingYouTubeApi: false,

    /**
     * @private
     */
    youTubePlayer: null,

    /**
     * The ID of the YouTube video that's currently playing.
     * @private
     */
    videoId: null,

    /**
     * @type  {number}
     * @readonly
     * @public
     */
    hasInitialized: -1,

    /**
     * @private
     */
    changeMediaSourceOnInitialize: false,

    /**
     * The state of this page.
     *
     * @type  {?Object}
     * @public
     */
    state: null,

    /**
     * @public
     */
    autoplay: true,

    /**
     * @type  {boolean}
     * @public
     * @readonly
     */
    isPlaying: false,

    /**
     * @readonly
     * @public
     */
    playerState: "idle",

    /**
     * @private
     */
    observers: {
        update: ["state"]
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update        = frameThrottle(this.bindSafely(this.update));
            this.onPlayerReady = this.bindSafely(this.onPlayerReady);
            this.onTimeupdate  = this.bindSafely(this.onTimeupdate);
            this.onStateChange = this.bindSafely(this.onStateChange);
            this.onError       = this.bindSafely(this.onError);
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    update: function() {
        var state   = this.state || {},
            videoId = get(state, "watchingSession.source", null);

        // No videoId, nothing to play yet.
        if (!videoId) {
            this.clearPlayer();
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        // Load the YouTube Player API, initialize a new player if we don't
        // already have one or simply switch to the new media source if we
        // already have a fully initialized player.
        this.set("isLoadingYouTubeApi", true);
        Loader.load()
            .then(this.bindSafely(function() {
                switch (this.hasInitialized) {
                    case -1:
                        this.initializePlayer();
                        break;
                    case 0:
                        this.changeMediaSourceOnInitialize = true;
                        break;
                    case 1:
                        this.changeMediaSource();
                        break;
                    default:
                        break;
                }
            }))
            .catch(this.bindSafely(function(error) {
                logger.error(error);

                if (typeof Sentry !== "undefined" && error instanceof Error) {
                    Sentry.captureException(error);
                }

                this.onPlayerSetupError({
                    message: error.message
                });
            }))

            .finally(this.bindSafely(function() {
                this.set("isLoadingYouTubeApi", false);
            }));
    },

    /**
     * @param  {boolean}  wasLoading  The previous state.
     * @private
     */
    isLoadingYouTubeApiChanged: function(wasLoading) {
        var isLoading = this.isLoadingYouTubeApi;

        if (!wasLoading && isLoading) {
            this.controller.triggerHandler("onRequestShowLoadingGraphic");
        }
        else if (wasLoading && !isLoading) {
            this.controller.triggerHandler("onRequestHideLoadingGraphic");
        }
    },

    /**
     * Stops playback regardless of current ready state.
     *
     * @private
     */
    clearPlayer: function() {
        var player = this.getYouTubePlayerInstance();

        player && player.stopVideo && player.stopVideo();
    },

    /**
     * Creates a new YouTube Player instance and makes it play the requested
     * video.
     *
     * @private
     */
    initializePlayer: function() {
        var state        = this.state || {},
            $i18nService = this.app.$.i18nService,
            videoId      = get(state, "watchingSession.source", null),
            startCursor,
            embedCode,
            tmpNode,
            iframe,
            player;

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);
        this.set("hasInitialized", 0);

        // Get videoId
        this.set("videoId", videoId);

        // Missing embed code
        if (!videoId || videoId === "") {
            this.controller.triggerHandler("onMediaPlayerError", {
                type: "missing_video_source"
            }, this);
            this.set("isReady", -1);

            return;
        }

        // Resolve starting point
        startCursor = this.resolveStartCursor();
        this.set("startCursor", startCursor);

        // Build embed code
        embedCode   = quickMacroize('<iframe width="560" height="315" src="https://www.youtube-nocookie.com/embed/{$videoId}?enablejsapi=1&autoplay={$autoplay}&cc_lang_pref={$ccLangPref}&controls={$controls}&fs={$fullScreen}&hl={$uiLocale}&iv_load_policy={$annotations}&loop={$loop}&modestbranding={$modestbranding}&playsinline={$playsinline}&rel={$related}&start={$startCursor}" frameborder="0" allow="autoplay; encrypted-media;" allowfullscreen></iframe>', {
            videoId:        videoId,
            autoplay:       0,
            ccLangPref:     $i18nService.get("language"),
            controls:       1,
            fullScreen:     1,
            uiLocale:       $i18nService.get("locale"),
            annotations:    3, // Not shown by default
            loop:           0,
            modestbranding: 1,
            playsinline:    0, // Play inline on IOS in some cases
            related:        0, // Only from the same channel. Cannot disable fully
            startCursor:    startCursor
        });

        // Build iframe
        tmpNode           = document.createElement("div");
        tmpNode.innerHTML = embedCode;
        iframe            = tmpNode.firstElementChild;
        this.set("iframe", iframe);

        // Connect player
        player = new YT.Player(iframe, {
            title:  get(state, "media.meta.title", null) || "",
            events: {
                onReady:       this.onPlayerReady,
                onStateChange: this.onStateChange,
                onError:       this.onError
            }
        });
        this.set("youTubePlayer", player);

        // Append iframe to node
        this.hasNode().appendChild(iframe);
    },

    /**
     * Set the new videoID as new source for the existing player to play.
     *
     * @private
     */
    changeMediaSource: function() {
        var state   = this.state || {},
            videoId = get(state, "watchingSession.source", null),
            startCursor,
            player;

        // Already showing correct video
        if (videoId === this.videoId) {
            try {
                this.get("autoplay") && this.play();
            }
            catch (error) {
                // Apparently autoplay wasn't allowed. Whatever
            }

            return;
        }

        // Let's do this
        this.resetPlayerState();
        this.set("isReady", 0);

        // Find Player Instance
        player  = this.getYouTubePlayerInstance();

        if (!player) {
            this.controller.triggerHandler("onMediaPlayerError", {
                type:    "player_error",
                message: "Missing YouTubePlayer Instance"
            }, this);
            this.set("isReady", -1);

            return;
        }


        // Set new video
        startCursor = this.resolveStartCursor();
        this.set("videoId", videoId);
        this.set("startCursor", startCursor);
        player.loadVideoById(videoId, startCursor);
    },

    /**
     * Verify that this player can be (re)used to play the supplied media.
     *
     * @param    {?Object}  mediaData    The media data
     * @param    {?Object}  sessionData  The watching session
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function(mediaData, sessionData) {
        var mediaType    = get(mediaData, "type", null),
            assets       = get(mediaData, "assets", []),
            primaryAsset = assets.find(function(asset) {
                return asset.type === "primary";
            }) || null,
            format       = get(primaryAsset, "format", null),
            platform     = get(sessionData, "platform", null);

        return this.isReusable
            && ["episode", "video"].includes(mediaType)
            && format === "external_video"
            && platform === "youtube";
    },

    /**
     * Determines the position from which to start the video.
     *
     * @returns {number}  The position at which to start the video.
     * @private
     */
    resolveStartCursor: function() {
        var state         = this.state || {},
            sessionCursor = get(state, "watchingSession.cursor", 0),
            paramsCursor  = get(state, "params.cursor", null),
            duration      = get(state, "asset.duration", 0),
            cursor;

        cursor = paramsCursor !== null
            ? parseInt(paramsCursor, 10)
            : parseInt(sessionCursor, 10);

        return cursor >= 5 && cursor < duration - 10 ? cursor : 0;
    },

    /**
     * Get an instance of YouTubePlayer.
     *
     * @return {Object}  The YouTubePlayer instance
     */
    getYouTubePlayerInstance: function() {
        return this.get("youTubePlayer") || null;
    },

    // YouTubePlayer Event Handlers
    // --------------------------------

    /**
     * Is called when the setup of the YouTube Player instance has failed.
     *
     *  - Resets state
     *  - Sends error event.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onPlayerSetupError: function(event) {
        this.resetPlayerState();
        this.set("isReusable", false);
        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "player_setup_error",
            message: event.message
        });
    },

    /**
     * Is called by the newly created YouTubePlayer instance once it is ready.
     *
     * @private
     */
    onPlayerReady: function() {
        var state   = this.state || {},
            videoId;

        this.set("isReady", 1);
        this.set("hasInitialized", 1);

        // A switch of media source is already requested
        if (this.changeMediaSourceOnInitialize) {
            videoId = get(state, "watchingSession.source", null);

            // Only switch if the video is actually different
            if (videoId !== this.videoId) {
                this.stop();
                this.changeMediaSource();
            }

            this.changeMediaSourceOnInitialize = false;
        }

        // No media switch. Try autoplaying the current video.
        else if (this.get("autoplay")) {
            try {
                this.play();
            }
            catch (error) {
                // Apparently autoplay wasn't allowed. Whatever
            }
        }
    },

    /**
     * Fires a dedicated handler for the new player state.
     *
     * @param  {Object}  event  The state change event
     * @private
     */
    onStateChange: function(event) {
        switch (event.data) {
            case -1:
                this.onIdle();
                break;
            case 0:
                this.onEnded();
                break;
            case 1:
                this.onPlay();
                break;
            case 2:
                this.onPause();
                break;
            case 3:
                this.onBuffer();
                break;
            case 5: // Video cued
            default:
                break;
        }
    },

    /**
     * @private
     */
    onIdle: function() {
        this.stopTimeupdate();
        this.set("isPlaying", false);
        this.set("playerState", "idle");
    },

    /**
     * @private
     */
    onPlay: function() {
        if (this.isReady !== 1) {
            this.set("isReady", 1);
        }

        this.startTimeupdate();

        // Ensure player is marked as playing.
        if (this.hasStarted) {
            this.set("isPlaying", true);
            this.set("playerState", "playing");
            this.app.$.autoplayTimerService.startTimer();
        }
    },

    /**
     * @private
     */
    onBuffer: function() {
        this.onTimeupdate();
        this.stopTimeupdate();
        this.set("playerState", "buffering");
    },

    /**
     * Reference to the interval that checks for time updates.
     *
     * @private
     */
    _timeUpdateInterval: null,

    /**
     * Start periodically checking the current playback position.
     *
     * @private
     */
    startTimeupdate: function() {

        if (!this._timeUpdateInterval) {
            this._timeUpdateInterval = setInterval(this.onTimeupdate, 100);
        }
        this.onTimeupdate();
    },

    /**
     * Stop periodically checking the current playback position.
     *
     * @private
     */
    stopTimeupdate: function() {

        if (this._timeUpdateInterval) {
            clearInterval(this._timeUpdateInterval);
            this._timeUpdateInterval = null;
        }
    },

    /**
     * Retrieve the current playback position and update the cursor.
     *
     * @private
     */
    onTimeupdate: function() {
        var player = this.getYouTubePlayerInstance(),
            cursor;

        // Might happen if a timer was still running.
        if (this.isReady === -1) {
            this.stopTimeupdate();

            return;
        }

        // Sometimes this feature isn't yet available when we start the time
        // update loop. If this is the case, just bail. Once the video is
        // playing this feature will be available and we can send accurate
        // pings.
        if (!player.getCurrentTime) {
            return;
        }

        cursor = player.getCurrentTime();

        if (!this.hasStarted && cursor >= this.get("startCursor")) {
            this.set("hasStarted", true);
            this.set("isPlaying", true);
            this.set("playerState", "playing");
            this.app.$.autoplayTimerService.startTimer();
        }
        this.set("cursor", cursor, true);
        this.set("isSeekable", true);
    },

    /**
     * @private
     */
    onPause: function() {
        this.onTimeupdate();
        this.stopTimeupdate();
        this.set("isPlaying", false);
        this.set("playerState", "paused");
    },

    /**
     * @private
     */
    onEnded: function() {
        var state     = this.state || {},
            nextMedia = get(state, "nextMedia", null);

        if (nextMedia && get(config, "autoplay.enabled", false)) {
            this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);
        }
        else {
            this.app.$.autoplayTimerService.stopTimer();
        }
    },

    /**
     * YouTubePlayer encountered an error.
     *
     * @param  {Object}  event  The error event
     * @private
     */
    onError: function(event) {
        this.controller.triggerHandler("onMediaPlayerError", {
            type:    "player_error",
            message: event.message
        });
    },

    /**
     * Tell the autoplayTimerService there was a user interaction whenever the
     * player gets paused or unpaused.
     *
     * @param  {string}  oldState  The old state
     * @param  {string}  newState  The new state
     * @private
     */
    playerStateChanged: function(oldState, newState) {
        if (oldState !== newState && (oldState === "paused" || newState === "paused")) {
            this.app.$.autoplayTimerService.resetTimer();
        }
    },

    // VideoPlayer API
    // --------------------------------

    /**
     * Start playing
     *
     * @public
     */
    play: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getYouTubePlayerInstance();
            player && player.playVideo();
        }
    },

    /**
     * Move to a certain cursor position
     *
     * @param  {number}  cursor  The cursor position to seek to
     *
     * @public
     */
    seek: function(cursor) {
        var player;

        if (this.get("isReady") === 1 && this.get("isSeekable")) {
            player = this.getYouTubePlayerInstance();
            player && player.seekTo(Math.round(cursor), true);
        }
        else {
            this.set("startCursor", cursor);
        }
    },

    /**
     * Pause the player
     *
     * @public
     */
    pause: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getYouTubePlayerInstance();
            player && player.pauseVideo();
        }
    },

    /**
     * Stop the player
     *
     * @public
     */
    stop: function() {
        var player;

        if (this.get("isReady") === 1) {
            player = this.getYouTubePlayerInstance();
            player && player.stopVideo();
        }
    },

    /**
     * Returns the current point on the player's timeline in seconds.
     *
     * @return  {?number}  The cursor position
     *
     * @public
     */
    getCursor: function() {
        return new Promise(function(resolve) {
            var player = this.getYouTubePlayerInstance();

            if (player && this.get("isReady") === 1) {
                resolve(player.getCurrentTime());
            }
            else {
                resolve(null);
            }
        });
    },

    /**
     * Returns the duration of the video in seconds.
     *
     * @return  {?number}  The duration of the current asset
     *
     * @public
     */
    getLength: function() {
        return new Promise(function(resolve) {
            var player = this.getYouTubePlayerInstance();

            if (player && this.get("isReady") === 1) {
                resolve(player.getDuration());
            }
            else {
                resolve(null);
            }
        });
    },

    // Destruction
    // --------------------------------

    /**
     * Iframes cannot be moved around the DOM without reloading. As a result,
     * unmounting it makes it no longer reusable.
     *
     * @private`
     */
    beforeUnmount: function() {
        this.set("isReusable", false);
    },

    /**
     * Destroys the current YouTubePlayer instance and resets state.
     *
     * @private
     */
    destroyPlayer: function() {
        var player = this.getYouTubePlayerInstance(),
            iframe = this.get("iframe");

        this.stop();

        if (player) {
            player.destroy();
        }
        this.set("youTubePlayer", null);

        if (iframe && iframe.parentNode) {
            iframe.remove();
        }
        this.set("iframe", null);

        this.resetPlayerState();
    },

    /**
     * Destroy the YouTube Player when this instance gets destroyed.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.destroyPlayer();

            // Ensures we trigger a request to hide the loading graphic.
            this.set("isLoadingYouTubeApi", false);
            sup.apply(this, arguments);
        };
    })

});
